import React, { useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import { Card, CardTitle } from '../../../../../components/Card';
import { InfoTooltip } from '../../../../../components/InfoTooltip';
import { LabelledSelect } from '../../../../../components/LabelledSelect';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { SelectOption } from '../../../../../components/Select';
import { RejectionBreakdown } from '../../../../../entities/applicant_tracking/analytics/RejectionBreakdown';
import { RejectionBreakdownService } from '../../../../../services/applicant_tracking/analytics/RejectionBreakdownService';
import { WidgetEmptyStateContent } from '../../../../survey_engine/WidgetEmptyStateContent';
import { FilterState } from '../../Filters';

interface PropTypes {
  filterState?: FilterState;
  groupBy: string;
  onGroupByChange: (groupBy: string) => void;
}

const GROUP_BY_OPTIONS: SelectOption[] = [
  { label: 'Department', value: 'department' },
  { label: 'Jobs', value: 'job' },
  { label: 'Source', value: 'source' },
];

export function RejectionBreakdownWidget(props: PropTypes) {
  const { groupBy, onGroupByChange } = props;
  const [data, setData] = useState<RejectionBreakdown | null>(null);

  useEffect(() => {
    if (!props.filterState) return;

    setData(null);
    const controller = new AbortController();

    RejectionBreakdownService.show(
      props.filterState,
      controller.signal,
      groupBy,
    )
      .then(setData)
      .catch((e) => {
        if (e.name !== 'AbortError') throw e;
      });

    return () => controller.abort();
  }, [JSON.stringify(props.filterState), groupBy]);

  const selectedGroupBy = GROUP_BY_OPTIONS.find((o) => o.value === groupBy);
  const groupByLabel = selectedGroupBy?.label ?? 'Group';

  return (
    <Card>
      <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
        <CardTitle>Rejection Rate</CardTitle>
        <InfoTooltip>
          Shows rejection data grouped by role, team, or source. Includes most
          common rejection reasons.
        </InfoTooltip>
      </div>
      <div className='mb-3' style={{ maxWidth: '220px' }}>
        <LabelledSelect
          label='Group by'
          options={GROUP_BY_OPTIONS}
          selected={selectedGroupBy}
          isClearable={false}
          onChange={(option) => onGroupByChange(option.value)}
          size='sm'
        />
      </div>
      <RejectionContent data={data} groupByLabel={groupByLabel} />
    </Card>
  );
}

function RejectionContent(props: {
  data: RejectionBreakdown | null;
  groupByLabel: string;
}) {
  if (!props.data) return <LoadingSpinner />;

  if (props.data.rows.length === 0) return <WidgetEmptyStateContent />;

  return <RejectionTable data={props.data} groupByLabel={props.groupByLabel} />;
}

function RejectionTable(props: {
  data: RejectionBreakdown;
  groupByLabel: string;
}) {
  return (
    <div className='table-responsive'>
      <Table className='mb-0 align-middle'>
        <thead>
          <tr className='border-bottom'>
            <th className='fw-bold py-3'>{props.groupByLabel}</th>
            <th className='fw-bold py-3'>Total Candidates</th>
            <th className='fw-bold py-3'>Rejections</th>
            <th className='fw-bold py-3'>Top Rejection Reason</th>
            <th className='fw-bold py-3'>Rejections (Top Reason)</th>
          </tr>
        </thead>
        <tbody>
          {props.data.rows.map((row) => (
            <tr key={row.groupId} className='border-bottom'>
              <td className='fw-bold py-3'>{row.groupName}</td>
              <td className='py-3'>{row.totalCandidates}</td>
              <td className='py-3'>
                {row.rejectionsCount}
                {row.totalCandidates > 0 && (
                  <span className='ms-1' style={{ color: '#A0AEC0' }}>
                    (
                    {(
                      (row.rejectionsCount / row.totalCandidates) *
                      100
                    ).toFixed(0)}
                    %)
                  </span>
                )}
              </td>
              <td className='py-3'>{row.topRejectionReason ?? '-'}</td>
              <td className='py-3'>
                {row.topReasonCount > 0 ? (
                  <>
                    {row.topReasonCount}
                    {row.totalCandidates > 0 && (
                      <span className='ms-1' style={{ color: '#A0AEC0' }}>
                        (
                        {(
                          (row.topReasonCount / row.totalCandidates) *
                          100
                        ).toFixed(0)}
                        %)
                      </span>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
